import { message } from "antd";

// ?no-inline: CKEditor.getUrl() prefixes its basePath onto any path lacking ":/", and a
// data: URI does not contain that sequence - so an inlined icon would be mangled into
// "<basePath>data:image/png;base64,...". These must stay real URLs.
import collapseToolbarIcon from "images/CKEditor/btcollapsetoolbar.png?no-inline";
import expandToolbarIcon from "images/CKEditor/btexpandtoolbar.png?no-inline";

/**
 * Ported from BT_WebApp/jQuery/ckeditor/config.js, which CKEditor auto-loaded as the
 * `customConfig` file sitting next to ckeditor.js. The npm engine ships its own stock
 * config.js with a toolbarGroups/removeButtons/format_tags setup we do not want, so
 * `customConfig` is blanked on the instance config and everything is applied from here.
 */

const COLLAPSE_PLUGIN = "btcollapsetoolbar";
const EXPAND_PLUGIN = "btexpandtoolbar";

/**
 * CKEDITOR.ENTER_BR / CKEDITOR.ENTER_P, inlined because the config object is built while
 * rendering, before ckeditor4-react has loaded the engine and defined the global.
 */
const ENTER_P = 1;
const ENTER_BR = 2;

/** The subset of CKEditor's runtime surface this module drives. */
interface ICKEditorInstance {
    id: string;
    config: { toolbarLocation: string };
    addCommand: (name: string, definition: { exec: (editor: ICKEditorInstance) => void }) => void;
    ui: {
        addButton: (
            name: string,
            definition: { label: string; command: string; toolbar: string },
        ) => void;
    };
    dataProcessor: {
        htmlFilter: {
            addRules: (rules: {
                elements: { $: (element: IHtmlFilterElement) => IHtmlFilterElement };
            }) => void;
        };
    };
}

export interface IHtmlFilterElement {
    name: string;
    attributes: { style?: string; width?: string; height?: string };
}

interface ICKEditorGlobal {
    plugins: {
        add: (name: string, definition: { init: (editor: ICKEditorInstance) => void }) => void;
    };
    skin: {
        /** `path` is used verbatim when it contains ":/", so a bundled asset URL works here. */
        addIcon: (name: string, path: string) => void;
    };
    on: {
        (event: "dialogDefinition", listener: (ev: IDialogDefinitionEvent) => void): void;
        (event: "instanceReady", listener: (ev: IInstanceReadyEvent) => void): void;
    };
}

interface IDialogDefinitionEvent {
    data: {
        name: string;
        definition: {
            getContents: (id: string) => {
                get: (id: string) => { default: string };
            };
        };
    };
}

interface IInstanceReadyEvent {
    editor: ICKEditorInstance;
}

/**
 * The npm engine is CKEditor's "Standard" build, so these are not compiled in the way they
 * were in the webforms "Full" build. They ship as lazy-loadable plugin folders, and naming
 * them here is what makes the Font, FontSize, TextColor, BGColor and Justify* toolbar buttons
 * resolve, and what restores the Advanced tab on the Table dialog (dialogadvtab, which
 * plugins/table/dialogs/table.js resolves defensively and otherwise silently omits).
 */
const BASE_EXTRA_PLUGINS = [
    COLLAPSE_PLUGIN,
    EXPAND_PLUGIN,
    "autogrow",
    "justify",
    "font",
    "colorbutton",
    "colordialog",
    "liststyle",
    "indentblock",
    "dialogadvtab",
].join(",");

const largeToolBar = [
    { name: "morestyles", items: ["Font", "FontSize"] },
    { name: "colors", items: ["TextColor", "BGColor"] },
    { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike"] },
    { name: "paragraph", items: ["JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock"] },
    { name: EXPAND_PLUGIN, items: [EXPAND_PLUGIN] },
    { name: COLLAPSE_PLUGIN, items: [COLLAPSE_PLUGIN] },
    "/",
    { name: "clipboard", items: ["JustifyBlock", "Cut", "Copy", "Paste", "-", "Undo", "Redo"] },
    { name: "links", items: ["Link", "Unlink", "Anchor"] },
    { name: "insert", items: ["Image", "Table", "HorizontalRule", " SpecialChar"] },
    { name: "tools", items: ["Maximize"] },
    "/",
    {
        name: "paragraph",
        items: ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "-", "Blockquote"],
    },
    { name: "styles", items: ["Styles", "Format"] },
    { name: "document", items: ["Source"] },
];

const TOGGLE_BUTTON_STYLE: Record<string, string> = {
    width: "26px",
    height: "26px",
    "box-sizing": "border-box",
    display: "flex",
    "align-content": "stretch",
    padding: "3px 6px",
    "text-decoration": "none",
};

const TOGGLE_BUTTON_ICON_STYLE: Record<string, string> = {
    "background-size": "11px",
    "background-position": "center center",
    "padding-top": "3px",
    height: "auto",
};

/**
 * CKEditor appends its skin CSS as an unlayered <link> at runtime, and unlayered normal
 * declarations outrank every cascade layer - so an equivalent rule in BTCKEditor.less
 * (auto-wrapped into appBtnet) would lose to the skin's own .cke_button rules.
 */
function applyStyles(selector: string, styles: Record<string, string>): void {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        for (const [property, value] of Object.entries(styles)) {
            element.style.setProperty(property, value);
        }
    });
}

function hideElement(element: Element | null | undefined): void {
    if (element instanceof HTMLElement) {
        element.style.display = "none";
    }
}

function toggleElement(element: Element | null | undefined): void {
    if (element instanceof HTMLElement) {
        element.style.display = element.style.display === "none" ? "" : "none";
    }
}

function hidePathItems(): void {
    document.querySelectorAll(".cke_path_item").forEach(hideElement);
}

/** The first `.cke_toolbar_break` separates the always-visible first row from the rest. */
function getFirstToolbarBreak(editor: ICKEditorInstance): Element | null {
    const toolbarSpace = document.getElementById(`${editor.id}_${editor.config.toolbarLocation}`);
    return toolbarSpace?.querySelector(".cke_toolbar_break") ?? null;
}

function applyFromBreakOnward(firstBreak: Element, apply: (element: Element) => void): void {
    let current: Element | null = firstBreak;
    while (current !== null) {
        apply(current);
        current = current.nextElementSibling;
    }
}

function expandCollapseToolbar(editorInstance: ICKEditorInstance): void {
    const firstBreak = getFirstToolbarBreak(editorInstance);
    if (firstBreak !== null) {
        toggleElement(firstBreak.previousElementSibling);
        toggleElement(firstBreak.previousElementSibling?.previousElementSibling);
        applyFromBreakOnward(firstBreak, toggleElement);
    }

    hidePathItems();
}

/**
 * Registers the icon against the bundled asset URL rather than the `icons: name` convention,
 * which would make CKEditor resolve `plugins/<name>/icons/<name>.png` against its own basePath
 * and force us to ship the PNGs into the engine's asset tree. Going through the bundler instead
 * keeps them in src/images with the app's other artwork, content-hashed and cached like any
 * other asset.
 */
function addToolbarTogglePlugin(
    ckeditor: ICKEditorGlobal,
    name: string,
    label: string,
    iconUrl: string,
): void {
    ckeditor.skin.addIcon(name, iconUrl);
    ckeditor.plugins.add(name, {
        init: (editor) => {
            editor.addCommand(name, {
                exec: (edt) => {
                    expandCollapseToolbar(edt);
                },
            });
            editor.ui.addButton(name, {
                label,
                command: name,
                toolbar: "insert",
            });
        },
    });
}

/**
 * Converts inline `width:Npx` / `height:Npx` CSS on `<img>` elements into real `width`/`height`
 * HTML attributes, then removes the style property if it ends up empty. Outlook ignores CSS
 * dimensions on images, so emailed content renders at the wrong size without this conversion.
 */
export function applyImageDimensionAttributes(element: IHtmlFilterElement): IHtmlFilterElement {
    if (element.name === "img") {
        const style = element.attributes.style;

        if (style) {
            let match = /(?:^|\s)width\s*:\s*(\d+)px/i.exec(style);
            const width = match && match[1];

            match = /(?:^|\s)height\s*:\s*(\d+)px/i.exec(style);
            const height = match && match[1];

            if (width) {
                element.attributes.style = style.replace(/(?:^|\s)width\s*:\s*(\d+)px;?/i, "");
                element.attributes.width = width;
            }

            if (height) {
                element.attributes.style = (element.attributes.style ?? "").replace(
                    /(?:^|\s)height\s*:\s*(\d+)px;?/i,
                    "",
                );
                element.attributes.height = height;
            }
        }
    }

    if (!element.attributes.style) {
        delete element.attributes.style;
    }

    return element;
}

function onInstanceReady(editorObj: IInstanceReadyEvent): void {
    applyStyles(`.cke_button__${COLLAPSE_PLUGIN}`, TOGGLE_BUTTON_STYLE);
    applyStyles(`.cke_button__${COLLAPSE_PLUGIN}_icon`, TOGGLE_BUTTON_ICON_STYLE);
    applyStyles(`.cke_button__${EXPAND_PLUGIN}`, TOGGLE_BUTTON_STYLE);
    applyStyles(`.cke_button__${EXPAND_PLUGIN}_icon`, TOGGLE_BUTTON_ICON_STYLE);

    hidePathItems();

    const editor = editorObj.editor;
    const firstBreak = getFirstToolbarBreak(editor);
    if (firstBreak !== null) {
        hideElement(firstBreak.previousElementSibling);
        applyFromBreakOnward(firstBreak, hideElement);
    }

    editor.addCommand("paste", {
        exec: () => {
            void message.warning(
                <span>
                    Press{" "}
                    <kbd>
                        <kbd>ctrl</kbd> + <kbd>v</kbd>
                    </kbd>{" "}
                    to paste. Your browser doesn't support pasting with the toolbar button or
                    context menu option.
                </span>,
                6,
            );
        },
    });

    editor.dataProcessor.htmlFilter.addRules({
        elements: { $: applyImageDimensionAttributes },
    });
}

let isRegistered = false;

/**
 * Registers the custom plugins and the global CKEditor hooks. `onBeforeLoad` fires once per
 * mounted editor, but `plugins.add` and `CKEDITOR.on` are page-global, so this is guarded.
 */
export function registerCKEditorCustomizations(editorGlobal: unknown): void {
    if (isRegistered) {
        return;
    }
    isRegistered = true;

    const ckeditor = editorGlobal as ICKEditorGlobal;

    addToolbarTogglePlugin(ckeditor, COLLAPSE_PLUGIN, "Collapse Toolbar", collapseToolbarIcon);
    addToolbarTogglePlugin(ckeditor, EXPAND_PLUGIN, "Expand Toolbar", expandToolbarIcon);

    ckeditor.on("dialogDefinition", (ev) => {
        if (ev.data.name === "link") {
            ev.data.definition.getContents("target").get("linkTargetType").default = "_blank";
        }
    });

    ckeditor.on("instanceReady", onInstanceReady);
}

/**
 * Builds the config passed to `CKEDITOR.replace()`, with any caller-supplied config layered
 * on top. `extraPlugins` is appended rather than overwritten so a caller adding a plugin
 * doesn't drop the toolbar toggles or the Standard-build backfills.
 */
export function buildCKEditorConfig(
    callerConfig?: Record<string, unknown>,
): Record<string, unknown> {
    const { extraPlugins: callerExtraPlugins, ...rest } = callerConfig ?? {};

    return {
        // Blank so CKEditor skips fetching the stock config.js that sits next to ckeditor.js.
        customConfig: "",

        toolbar: largeToolBar,
        allowedContent: true,
        tabSpaces: 4,
        sourceAreaTabSize: 4,
        enterMode: ENTER_BR,
        shiftEnterMode: ENTER_P,
        pasteFromWordPromptCleanup: true,
        pasteFromWordRemoveFontStyles: false,
        pasteFromWordNumberedHeadingToList: true,
        pasteFromWordRemoveStyles: true,
        toolbarLocation: "bottom",
        extraPlugins:
            typeof callerExtraPlugins === "string" && callerExtraPlugins
                ? `${BASE_EXTRA_PLUGINS},${callerExtraPlugins}`
                : BASE_EXTRA_PLUGINS,
        autoGrow_onStartup: true,
        autoGrow_minHeight: 100,
        autoGrow_maxHeight: 600,
        removePlugins: "scayt,wsc,elementspath,resize",

        // Use the browser's native spell checker instead of the WebSpellChecker (WProofreader)
        // service. browserContextMenuOnCtrl lets users reach the native spell-check suggestions
        // via Ctrl+right-click, since CKEditor otherwise overrides the browser context menu.
        disableNativeSpellChecker: false,
        browserContextMenuOnCtrl: true,

        ...rest,
    };
}
